import { useState, useCallback, useEffect } from "react";
import SectionContainer from "../../components/UI/SectionContainer";
import FingerprintSearchBox from "../../components/UI/FingerprintSearchBox";
import Pagination from "../../components/UI/Pagination";
import { openAlert, openConfirm, exportExcel, showPopup } from '../../utils/common';
import Spinner from '../../components/UI/Spinner';
import fingerprintService from '../../services/fingerprintService';

const FingerprintListPage = () => {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchKind, setSearchKind] = useState('0');
    const [keyword, setKeyword] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedIds, setSelectedIds] = useState([]);

    const pageSize = 10;
    const pageBlock = 5;

    const fetchList = useCallback(async (page = 1, skArg, kwArg) => {
      setLoading(true);
      try {
        const sk = skArg !== undefined ? Number(skArg) : 0;
        const kwState = kwArg !== undefined ? kwArg : '';
        
        const result = await fingerprintService.getFingerprintList(page, pageSize, sk, kwState);
        
        if (result.success) {
          setList(result.data.fingerprints || []);
          setTotalCount(result.data.pagination.totalCount || 0);
        } else {
          openAlert({ title: '알림', description: result.message || '데이터 로드에 실패했습니다.' });
        }
      } catch (error) { 
        console.error('fetchList error:', error); 
        openAlert({ title: '알림', description: error.message || '데이터 로드 중 오류가 발생했습니다.' });
      } finally {
        setLoading(false);
      }
    }, [pageSize]);

    useEffect(() => {
      fetchList(currentPage, searchKind, keyword);
    }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = (directKeyword) => { 
      setCurrentPage(1); 
      // Use directKeyword if provided (from suggestion select), otherwise use keyword from state
      fetchList(1, searchKind, directKeyword !== undefined ? directKeyword : keyword); 
    };
    const handleReset = () => { 
      setSearchKind('0'); 
      setKeyword(''); 
      setCurrentPage(1); 
      // Call fetchList directly with reset values, consistent with new handleSearch signature
      fetchList(1, '0', ''); 
    };

    // 체크박스 전체 선택
    const handleCheckAll = (e) => {
        if (e.target.checked) {
            // 모든 아이디를 한꺼번에 추가
            setSelectedIds(list.map((item) => `${item.userIdx},${item.deviceId}`));
        } else {
            setSelectedIds([]);
        }
    };

    // 체크박스 개별 선택
    const handleCheck = (id, checked) => {
        setSelectedIds(prev =>
            checked
                ? [...prev, id]
                : prev.filter(itemId => itemId !== id)
        );
    };

    // 선택된 지문 삭제 요청
    const requestDeleteFingerprint = useCallback(async () => {
        setLoading(true);
        try {
            // 개별 삭제를 순차적으로 수행
            for (const fingerprintId of selectedIds) {
                const [userIdx, deviceId] = fingerprintId.split(',');
                await fingerprintService.deleteFingerprint(userIdx, deviceId);
            }
            
            setSelectedIds([]);
            openAlert({ 
                title: '알림', 
                description: `${selectedIds.length}개의 지문 데이터가 삭제되었습니다.`, 
                response_method: "refreshFingerprintList" 
            });
        } catch (e) { 
            const errorMessage = e.response?.data?.message || e.message || '지문 삭제 중 오류가 발생했습니다.';
            openAlert({ title: '알림', description: errorMessage });
        } finally {
            setLoading(false);
        }
    }, [selectedIds]);

    // 삭제 확인 모달
    const requestDeleteFingerprintConfirm = () => {
        if(selectedIds.length === 0){
            openAlert({title:"알림", description: "삭제할 지문을 선택해주세요."});
            return;
        }
        openConfirm({
            title: "삭제",
            description: `선택하신 ${selectedIds.length}개의 지문 데이터를 삭제하시겠습니까?`,
            positive_method : "requestDeleteFingerprint",
            positive_btn : "확인",
            negative_btn : "취소",
        });
    };

    // 목록 새로고침
    const refreshFingerprintList = () => {
        fetchList(currentPage, searchKind, keyword);
    };

    const showUserFingerprint = (idx) => {
      showPopup(`/user/fingerprint/${idx}`, 'userinfo');
    };

    // window 함수 등록
    useEffect(() => {
        window.refreshFingerprintList = refreshFingerprintList;
        window.requestDeleteFingerprint = requestDeleteFingerprint;
        return () => {
            delete window.refreshFingerprintList;
            delete window.requestDeleteFingerprint;
        }
    }, [refreshFingerprintList, requestDeleteFingerprint]);

    return (
        <SectionContainer title="지문관리">
            <FingerprintSearchBox
              value={keyword}
              onChange={setKeyword}
              searchKind={searchKind}
              onSelect={setSearchKind}
              onSearch={handleSearch}
              onReset={handleReset}
              onSuggestSelect={selectedVal => { 
                setKeyword(selectedVal); // Update the keyword state
                handleSearch(selectedVal); // And immediately search with this selected value
              }}
              totalCount={totalCount}
              onDelete={requestDeleteFingerprintConfirm}
              deleteButtonConfig={{
                label: "지문 삭제",
                count: selectedIds.length,
                style: {backgroundColor: '#ff2b2b'}
              }}
            />
            <div className="content_area">
              <Spinner visible={loading} />
              <div className="table_area">
                <div className="table">
                  <table className="fingerprint-list-table">
                    <thead>
                      <tr>
                        <th><input type="checkbox" checked={selectedIds.length === list.length && list.length > 0} onChange={handleCheckAll} /></th>
                        <th>성명</th>
                        <th>아이디</th>
                        <th>이메일</th>
                        <th>Manufacturer</th>
                        <th>Model</th>
                        <th>Serial</th>
                        <th>Index</th>
                        <th>가입일</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!loading && list.length === 0 ? (
                        <tr><td colSpan="9">회원 내역이 존재하지 않습니다.</td></tr>
                      ) : (
                        list.map((item, idx) => (
                          <tr key={idx}>
                            <td style={{cursor: 'default'}}>
                              <input type="checkbox" 
                                checked={selectedIds.includes(`${item.userIdx},${item.deviceId}`)}
                                onChange={(e) => {
                                  handleCheck(`${item.userIdx},${item.deviceId}`, e.target.checked);
                                  e.stopPropagation();
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className="clickable" onClick={()=>showUserFingerprint(item.userIdx)} style={{cursor: 'pointer'}}>{item.userName}</td>
                            <td style={{cursor: 'default'}}>{item.userId}</td>  
                            <td style={{cursor: 'default'}}>{item.userEmail}</td>
                            <td style={{cursor: 'default'}}>{item.manufacturerName}</td>
                            <td style={{cursor: 'default'}}>{item.modelNumber}</td>
                            <td style={{cursor: 'default'}}>{item.serialNumber}</td>
                            <td style={{cursor: 'default'}}>{item.fingerprintIdx}</td>
                            <td style={{cursor: 'default'}}>{item.dateAdded}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <Pagination currentPage={currentPage} totalCount={totalCount} pageSize={pageSize} pageBlock={pageBlock} onPageChange={setCurrentPage} />
        </SectionContainer>
    );
}

export default FingerprintListPage; 